// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * SchemaCatalogPanel — editable schema catalog.
 *
 * Schema IDs are namespaced: "system.{name}" or "{app_id}.{name}"
 * Schemas are stored in DynamoDB and shared across all workspaces.
 */
import { useState, useEffect, useMemo, type FC } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Eye, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableCard, TableCardEmpty, TableCardLoading } from '@/components/ui/table-card'
import { SegmentedTabs } from '@/components/ui/segmented-tabs'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useServices } from '@/app-shell/services'
import { useAuth } from '@/subsystems/workspaces'
import { useApplications } from '@/subsystems/workspaces/services'
import type { SchemaRecord, CreateSchemaRequest, UpdateSchemaRequest } from '../types'

type SchemaScope = 'system' | 'app'
type ScopeFilter = 'all' | SchemaScope

function getScope(schemaId: string): SchemaScope {
  return schemaId.startsWith('system.') ? 'system' : 'app'
}

const SCOPE_FILTERS: { id: ScopeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'system', label: 'System' },
  { id: 'app', label: 'Application' },
]

/** Extract namespace from schema ID (e.g., "system" from "system.findings") */
function getNamespace(schemaId: string): string {
  const dotIndex = schemaId.indexOf('.')
  return dotIndex > 0 ? schemaId.substring(0, dotIndex) : 'unknown'
}

/** Extract name from schema ID (e.g., "findings" from "system.findings") */
function getName(schemaId: string): string {
  const dotIndex = schemaId.indexOf('.')
  return dotIndex > 0 ? schemaId.substring(dotIndex + 1) : schemaId
}

interface SchemaCatalogPanelProps {
  readonly?: boolean
  /** When provided, list via the workspace-scoped endpoint instead of /admin/schemas. */
  workspaceScope?: { orgId: string; wsId: string }
}

export const SchemaCatalogPanel: FC<SchemaCatalogPanelProps> = ({
  readonly = false,
  workspaceScope,
}) => {
  const services = useServices()
  const queryClient = useQueryClient()
  const { isGlobalAdmin } = useAuth()

  const [editingSchema, setEditingSchema] = useState<SchemaRecord | null>(null)
  const [deletingSchema, setDeletingSchema] = useState<SchemaRecord | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all')
  const [appFilter, setAppFilter] = useState<string>('all')

  const { data: applications = [] } = useApplications(isGlobalAdmin)

  // Fetch schemas — workspace-scoped for non-admin views, full admin catalog otherwise.
  const { data: schemas = [], isLoading } = useQuery({
    queryKey: workspaceScope
      ? ['workspace-schemas', workspaceScope.orgId, workspaceScope.wsId]
      : ['admin-schemas'],
    queryFn: () =>
      workspaceScope
        ? services.schemaCatalog.listForWorkspace(workspaceScope.orgId, workspaceScope.wsId)
        : services.schemaCatalog.list(),
    enabled: !workspaceScope || (!!workspaceScope.orgId && !!workspaceScope.wsId),
    staleTime: 5 * 60_000,
  })

  const filteredSchemas = useMemo(() => {
    let result = schemas
    if (scopeFilter !== 'all') {
      result = result.filter((s) => getScope(s.schemaId) === scopeFilter)
    }
    if (isGlobalAdmin && scopeFilter === 'app' && appFilter !== 'all') {
      result = result.filter((s) => s.schemaId.startsWith(`${appFilter}.`))
    }
    return result
  }, [schemas, scopeFilter, appFilter, isGlobalAdmin])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (request: CreateSchemaRequest) => services.schemaCatalog.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-schemas'] })
      toast.success('Schema created')
      setCreateDialogOpen(false)
    },
    onError: (error) => {
      toast.error('Failed to create schema', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ schemaId, request }: { schemaId: string; request: UpdateSchemaRequest }) =>
      services.schemaCatalog.update(schemaId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-schemas'] })
      toast.success('Schema updated')
      setEditingSchema(null)
    },
    onError: (error) => {
      toast.error('Failed to update schema', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (schemaId: string) => services.schemaCatalog.delete(schemaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-schemas'] })
      toast.success('Schema deleted')
      setDeletingSchema(null)
    },
    onError: (error) => {
      toast.error('Failed to delete schema', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    },
  })

  const emptyAction = !readonly && schemas.length === 0 && (
    <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
      <Plus className="h-4 w-4 mr-1" />
      New Schema
    </Button>
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <SegmentedTabs tabs={SCOPE_FILTERS} value={scopeFilter} onChange={setScopeFilter} />
        {isGlobalAdmin && scopeFilter === 'app' && (
          <div className="w-56">
            <Select value={appFilter} onValueChange={setAppFilter}>
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All applications</SelectItem>
                {applications.map((a) => (
                  <SelectItem key={a.appId} value={a.appId}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <span className="flex-1" />
        {!readonly && (
          <Button size="sm" onClick={() => setCreateDialogOpen(true)} data-testid="schemas-new">
            <Plus className="h-4 w-4 mr-1" />
            New Schema
          </Button>
        )}
      </div>

      <TableCard
        title="Extraction schemas"
        meta={
          filteredSchemas.length > 0
            ? `${filteredSchemas.length} schema${filteredSchemas.length === 1 ? '' : 's'}`
            : undefined
        }
      >
        {isLoading ? (
          <TableCardLoading label="Loading schemas…" />
        ) : filteredSchemas.length === 0 ? (
          schemas.length === 0 ? (
            <TableCardEmpty
              title="No schemas yet"
              description="Create a schema to define the structured output the AI produces. Datasets then reference it to tell an agent what to extract."
              action={emptyAction}
            />
          ) : (
            <TableCardEmpty
              title="No schemas match this filter"
              description="Try a different scope or application filter."
            />
          )
        ) : (
          <SchemaTable
            schemas={filteredSchemas}
            readonly={readonly}
            onEdit={setEditingSchema}
            onDelete={setDeletingSchema}
          />
        )}
      </TableCard>

      {/* Create Dialog */}
      <SchemaFormDialog
        open={createDialogOpen}
        title="Create Schema"
        schema={null}
        isLoading={createMutation.isPending}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={(data) => createMutation.mutate(data as CreateSchemaRequest)}
      />

      {/* Edit / View Dialog */}
      <SchemaFormDialog
        open={!!editingSchema}
        title={readonly ? 'Schema Details' : 'Edit Schema'}
        schema={editingSchema}
        readonly={readonly}
        isLoading={updateMutation.isPending}
        onClose={() => setEditingSchema(null)}
        onSubmit={(data) =>
          editingSchema &&
          updateMutation.mutate({ schemaId: editingSchema.schemaId, request: data })
        }
      />

      {/* Delete Confirmation Dialog */}
      <DeleteSchemaDialog
        schema={deletingSchema}
        isLoading={deleteMutation.isPending}
        onClose={() => setDeletingSchema(null)}
        onConfirm={() => deletingSchema && deleteMutation.mutate(deletingSchema.schemaId)}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface SchemaTableProps {
  schemas: SchemaRecord[]
  readonly: boolean
  onEdit: (schema: SchemaRecord) => void
  onDelete: (schema: SchemaRecord) => void
}

const SchemaTable: FC<SchemaTableProps> = ({ schemas, readonly, onEdit, onDelete }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="hidden sm:table-cell">Namespace</TableHead>
        <TableHead className="hidden sm:table-cell">Name</TableHead>
        <TableHead>
          <span className="sm:hidden">Schema</span>
          <span className="hidden sm:inline">Label</span>
        </TableHead>
        <TableHead className="hidden md:table-cell">Description</TableHead>
        <TableHead className="w-20" />
      </TableRow>
    </TableHeader>
    <TableBody>
      {schemas.map((schema) => (
        <TableRow key={schema.schemaId} data-testid={`schema-row-${schema.schemaId}`}>
          <TableCell className="hidden sm:table-cell">
            <span className="rounded-md border border-slate-200 bg-slate-100 px-1.5 py-[3px] font-mono text-[11px] text-slate-600">
              {getNamespace(schema.schemaId)}
            </span>
          </TableCell>
          {/* Names can be long ids with no natural break points (underscores,
              digits); break-all lets the column shrink instead of pushing the
              label and action columns out of the viewport. On phones the name
              column would wrap to a few characters per line, so it collapses
              under the label instead. */}
          <TableCell className="hidden font-mono text-xs whitespace-normal break-all sm:table-cell">
            {getName(schema.schemaId)}
          </TableCell>
          <TableCell className="font-[550] whitespace-normal">
            {schema.label}
            <div className="mt-0.5 font-mono text-[11px] font-normal text-slate-500 break-all sm:hidden">
              {getName(schema.schemaId)}
            </div>
          </TableCell>
          {/* Auto table layout ignores max-width on the cell itself; the inner
              block enforces it so long descriptions truncate instead of
              stretching the table past the card edge. */}
          <TableCell className="hidden text-slate-500 md:table-cell">
            <div className="max-w-xs truncate">{schema.description ?? '—'}</div>
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={readonly ? 'View schema' : 'Edit schema'}
                onClick={() => onEdit(schema)}
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
              {!readonly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  aria-label="Delete schema"
                  onClick={() => onDelete(schema)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

interface SchemaFormDialogProps {
  open: boolean
  title: string
  schema: SchemaRecord | null
  isLoading: boolean
  readonly?: boolean
  onClose: () => void
  onSubmit: (data: CreateSchemaRequest | UpdateSchemaRequest) => void
}

const SchemaFormDialog: FC<SchemaFormDialogProps> = ({
  open,
  title,
  schema,
  isLoading,
  readonly = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = !schema
  const [formData, setFormData] = useState<{
    schemaId: string
    label: string
    description: string
    jsonSchema: string
    promptTemplate: string
    exactTextFields: string
  }>({
    schemaId: '',
    label: '',
    description: '',
    jsonSchema: '{\n  "type": "object",\n  "properties": {}\n}',
    promptTemplate: '',
    exactTextFields: '',
  })

  // Reset form when dialog opens or schema changes
  useEffect(() => {
    if (open) {
      if (schema) {
        setFormData({
          schemaId: schema.schemaId,
          label: schema.label,
          description: schema.description ?? '',
          jsonSchema: JSON.stringify(schema.jsonSchema, null, 2),
          promptTemplate: schema.promptTemplate ?? '',
          exactTextFields: (schema.exactTextFields ?? []).join(', '),
        })
      } else {
        setFormData({
          schemaId: '',
          label: '',
          description: '',
          jsonSchema: '{\n  "type": "object",\n  "properties": {}\n}',
          promptTemplate: '',
          exactTextFields: '',
        })
      }
    }
  }, [open, schema])

  // Validate JSON schema
  const schemaValidation = useMemo(() => {
    try {
      const parsed = JSON.parse(formData.jsonSchema)
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { valid: false, error: 'Schema must be an object', parsed: null }
      }
      if (!parsed.type) {
        return { valid: false, error: 'Schema must have a "type" property', parsed: null }
      }
      return { valid: true, error: null, parsed }
    } catch (e) {
      const message = e instanceof SyntaxError ? e.message : 'Invalid JSON'
      return { valid: false, error: message, parsed: null }
    }
  }, [formData.jsonSchema])

  // Validate schema ID format for create
  const schemaIdValidation = useMemo(() => {
    if (!isCreate) return { valid: true, error: null }
    if (!formData.schemaId) return { valid: false, error: null }
    if (!formData.schemaId.includes('.')) {
      return { valid: false, error: 'Must be namespaced: "system.name" or "app-id.name"' }
    }
    return { valid: true, error: null }
  }, [isCreate, formData.schemaId])

  const handleSubmit = () => {
    if (!schemaValidation.valid || !schemaValidation.parsed) return
    if (isCreate && !schemaIdValidation.valid) return

    const parsedExactTextFields = formData.exactTextFields
      .split(',')
      .map((field) => field.trim())
      .filter(Boolean)
    const exactTextFields = parsedExactTextFields.length ? parsedExactTextFields : null

    if (isCreate) {
      onSubmit({
        schemaId: formData.schemaId,
        label: formData.label,
        description: formData.description || null,
        jsonSchema: schemaValidation.parsed,
        promptTemplate: formData.promptTemplate || null,
        exactTextFields,
      })
    } else {
      onSubmit({
        label: formData.label,
        description: formData.description || null,
        jsonSchema: schemaValidation.parsed,
        promptTemplate: formData.promptTemplate || null,
        exactTextFields,
      })
    }
  }

  const canSubmit =
    schemaValidation.valid &&
    (!isCreate || (schemaIdValidation.valid && formData.schemaId && formData.label))

  return (
    <Dialog open={open} onOpenChange={(openState) => !openState && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {isCreate ? 'Create a new schema' : 'Edit schema details'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {isCreate ? (
            <div className="space-y-2">
              <Label htmlFor="schemaId">Schema ID</Label>
              <Input
                id="schemaId"
                placeholder="system.my-schema or app-id.my-schema"
                value={formData.schemaId}
                onChange={(e) => setFormData({ ...formData, schemaId: e.target.value })}
                className={schemaIdValidation.error ? 'border-destructive' : ''}
              />
              {schemaIdValidation.error ? (
                <p className="text-xs text-destructive">{schemaIdValidation.error}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Format: namespace.name (e.g., system.findings or contract-review.score)
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Schema ID</Label>
              <p className="font-mono text-sm">{formData.schemaId}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              placeholder="Human-readable name"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              readOnly={readonly}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              placeholder="Brief description of the schema"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              readOnly={readonly}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jsonSchema">JSON Schema</Label>
            <Textarea
              id="jsonSchema"
              className={`font-mono text-xs min-h-[200px] ${!schemaValidation.valid ? 'border-destructive' : ''}`}
              value={formData.jsonSchema}
              onChange={(e) => setFormData({ ...formData, jsonSchema: e.target.value })}
              readOnly={readonly}
            />
            {!schemaValidation.valid && (
              <p className="text-xs text-destructive">{schemaValidation.error}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="promptTemplate">Prompt Template (optional)</Label>
            <Textarea
              id="promptTemplate"
              className="text-sm min-h-[100px]"
              placeholder="Instructions for the LLM when using this schema"
              value={formData.promptTemplate}
              onChange={(e) => setFormData({ ...formData, promptTemplate: e.target.value })}
              readOnly={readonly}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="exactTextFields">Exact Text Fields (optional)</Label>
            <Input
              id="exactTextFields"
              placeholder="contract_text, quoted_text"
              value={formData.exactTextFields}
              onChange={(e) => setFormData({ ...formData, exactTextFields: e.target.value })}
              readOnly={readonly}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated finding fields holding verbatim source text. The page /
              hallucination filter runs only on these; leave blank to disable it.
            </p>
          </div>
        </div>
        <DialogFooter>
          {readonly ? (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isLoading || !canSubmit}>
                {isLoading ? 'Saving...' : isCreate ? 'Create' : 'Save'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface DeleteSchemaDialogProps {
  schema: SchemaRecord | null
  isLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

const DeleteSchemaDialog: FC<DeleteSchemaDialogProps> = ({
  schema,
  isLoading,
  onClose,
  onConfirm,
}) => (
  <Dialog open={!!schema} onOpenChange={(open) => !open && onClose()}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete Schema</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete the schema &quot;{schema?.schemaId}&quot;? This action
          cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} disabled={isLoading}>
          {isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
